import {
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  signInWithCustomToken,
  type MultiFactorError,
  type MultiFactorResolver,
  type PhoneMultiFactorInfo,
} from 'firebase/auth';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Lock,
  Mail,
  AlertTriangle,
  CheckCircle2,
  KeyRound,
  Terminal,
  Eye,
  EyeOff,
  HelpCircle,
  X,
  Flame,
  Smartphone,
} from 'lucide-react';
import {
  fetchAdminMeApi,
  getAdminToken,
  setAdminToken,
  clearAdminToken,
  setStoredAdminInfo,
  sendAdminMfaOtpApi,
  verifyAdminMfaOtpApi,
  adminDirectEmailLoginApi,
  setAdminMfaSession,
  getAdminMfaSession,
  clearAdminMfaSession,
} from '../lib/adminService';
import { PairUpLogo } from '../components/common/PairUpLogo';
import {
  adminSignInWithFirebase,
  checkCurrentUserAdminStatus,
  sendAdminPasswordReset,
  signOutUser,
  auth,
} from '../lib/firebase';

function formatFirebaseAdminAuthError(err: any): string {
  const code = String(err?.code || '');
  if (
    code === 'auth/invalid-credential' ||
    code === 'auth/wrong-password' ||
    code === 'auth/user-not-found'
  ) {
    return 'Invalid administrator email or password. Please verify your credentials and try again.';
  }
  if (code === 'auth/invalid-email') {
    return 'Please enter a valid administrator email address.';
  }
  if (code === 'auth/user-disabled') {
    return 'This administrator account has been disabled. Contact the Super Administrator.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many failed sign-in attempts. Please wait a few minutes or reset your password.';
  }
  return err?.message || 'Server authentication failure.';
}

export const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberSession, setRememberSession] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [alreadyAuthed, setAlreadyAuthed] = useState(false);
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetStatusMsg, setResetStatusMsg] = useState<string | null>(null);
  const [resetErrorMsg, setResetErrorMsg] = useState<string | null>(null);

  // Admin MFA / 2FA state
  const [mfaStep, setMfaStep] = useState<
    'NONE' | 'FIREBASE_TOTP' | 'FIREBASE_PHONE' | 'SERVER_SMS_OTP'
  >('NONE');
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [selectedPhoneHint, setSelectedPhoneHint] = useState<PhoneMultiFactorInfo | null>(null);
  const [firebaseVerificationId, setFirebaseVerificationId] = useState<string | null>(null);
  const [isSendingFirebasePhoneOtp, setIsSendingFirebasePhoneOtp] = useState(false);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);

  const [mfaPhone, setMfaPhone] = useState('');
  const [mfaMaskedPhone, setMfaMaskedPhone] = useState<string | null>(null);
  const [mfaVerificationId, setMfaVerificationId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaOtpSent, setMfaOtpSent] = useState(false);
  const [pendingAdminRole, setPendingAdminRole] = useState<string | null>(null);

  const clearRecaptchaVerifier = () => {
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch {
        // ignore clearing errors
      }
      recaptchaVerifierRef.current = null;
    }
    const container =
      recaptchaContainerRef.current ||
      document.getElementById('firebase-phone-recaptcha-container');
    if (container) {
      container.innerHTML = '';
    }
  };

  const createFreshRecaptchaVerifier = (): RecaptchaVerifier => {
    clearRecaptchaVerifier();
    const container =
      recaptchaContainerRef.current ||
      document.getElementById('firebase-phone-recaptcha-container');
    if (!container) {
      throw new Error('Firebase phone verification container is not mounted in the DOM.');
    }
    container.innerHTML = '';
    // Append a pristine child container so grecaptcha.render never encounters a previously-tagged DOM element
    const targetElement = document.createElement('div');
    container.appendChild(targetElement);

    const appVerifier = new RecaptchaVerifier(auth, targetElement, {
      size: 'invisible',
    });
    recaptchaVerifierRef.current = appVerifier;
    return appVerifier;
  };

  useEffect(() => {
    return () => {
      clearRecaptchaVerifier();
    };
  }, []);

  useEffect(() => {
    // Check if admin is already authenticated
    const token = getAdminToken();
    if (token) {
      fetchAdminMeApi()
        .then((res) => {
          if (res?.success) {
            setAlreadyAuthed(true);
          }
        })
        .catch(() => {
          // Token expired or invalid
        });
    }
  }, []);

  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setErrorMsg('Please enter both administrative email and password.');
      return;
    }

    setIsLoading(true);
    setErrorMsg(null);

    try {
      // 1. Direct Server-Verified Email/Password Bridge (Email & Password Only)
      const directRes = await adminDirectEmailLoginApi(email.trim(), password);
      if (directRes?.success && directRes?.customToken) {
        const userCred = await signInWithCustomToken(auth, directRes.customToken);
        const freshIdToken = await userCred.user.getIdToken(true);

        setAdminToken(freshIdToken);
        setStoredAdminInfo({
          email: userCred.user.email || email.trim(),
          name: userCred.user.displayName || directRes.admin?.name || 'Administrator',
          role: directRes.admin?.role || 'SUPER_ADMIN',
          source: 'firebase_auth',
          authMethod: 'PASSWORD',
          mfaVerified: false,
        });

        // If server indicates MFA is required, transition directly to SMS OTP without navigating to dashboard
        const isMfaRequired = directRes.mfaRequired === true || directRes.admin?.mfaRequired === true;
        if (isMfaRequired) {
          const role = directRes.admin?.role || 'SUPER_ADMIN';
          setPendingAdminRole(role);
          setMfaStep('SERVER_SMS_OTP');
          setErrorMsg(null);
          setIsLoading(false);

          const phoneCandidate = userCred.user.phoneNumber || directRes.admin?.phone || undefined;
          if (phoneCandidate) {
            setMfaPhone(phoneCandidate);
          }

          try {
            const smsRes = await sendAdminMfaOtpApi(phoneCandidate);
            setMfaOtpSent(true);
            setMfaMaskedPhone(smsRes?.maskedPhone || null);
            setMfaVerificationId(smsRes?.verificationId || null);
          } catch (smsErr: any) {
            setErrorMsg(
              smsErr?.code === 'ADMIN_PHONE_REQUIRED'
                ? 'Enter your registered administrator mobile number to receive a 2FA code.'
                : smsErr?.message || 'Enter your registered administrator mobile number to receive a 2FA code.'
            );
          }
          return;
        }

        try {
          const meRes = await fetchAdminMeApi();
          if (!meRes?.success || !meRes?.admin) {
            throw new Error('Server-side administrator session verification did not succeed.');
          }

          navigate('/admin');
          return;
        } catch (meErr: any) {
          if (
            meErr?.code === 'ADMIN_MFA_REQUIRED' ||
            (meErr?.status === 403 &&
              (meErr?.code === 'ADMIN_MFA_REQUIRED' ||
                String(meErr?.message || '').toLowerCase().includes('multi-factor') ||
                String(meErr?.message || '').toLowerCase().includes('2fa')))
          ) {
            const role = directRes.admin?.role || 'SUPER_ADMIN';
            setPendingAdminRole(role);
            setMfaStep('SERVER_SMS_OTP');
            setErrorMsg(null);
            setIsLoading(false);

            const phoneCandidate = userCred.user.phoneNumber || directRes.admin?.phone || undefined;
            if (phoneCandidate) {
              setMfaPhone(phoneCandidate);
            }

            try {
              const smsRes = await sendAdminMfaOtpApi(phoneCandidate);
              setMfaOtpSent(true);
              setMfaMaskedPhone(smsRes?.maskedPhone || null);
              setMfaVerificationId(smsRes?.verificationId || null);
            } catch (smsErr: any) {
              setErrorMsg(
                smsErr?.code === 'ADMIN_PHONE_REQUIRED'
                  ? 'Enter your registered administrator mobile number to receive a 2FA code.'
                  : smsErr?.message || 'Enter your registered administrator mobile number to receive a 2FA code.'
              );
            }
            return;
          }
          throw meErr;
        }
      }
    } catch (directErr: any) {
      setIsLoading(false);
      if (
        directErr?.code === 'ADMIN_MFA_REQUIRED' ||
        (directErr?.status === 403 &&
          (directErr?.code === 'ADMIN_MFA_REQUIRED' ||
            String(directErr?.message || '').toLowerCase().includes('multi-factor') ||
            String(directErr?.message || '').toLowerCase().includes('2fa')))
      ) {
        setPendingAdminRole('SUPER_ADMIN');
        setMfaStep('SERVER_SMS_OTP');
        setErrorMsg(null);
        return;
      }
      if (
        directErr?.code === 'INVALID_CREDENTIALS' ||
        directErr?.code === 'ADMIN_FORBIDDEN' ||
        directErr?.code === 'ADMIN_ACCOUNT_DISABLED' ||
        directErr?.code === 'ADMIN_EMAIL_UNVERIFIED' ||
        directErr?.code === 'TOO_MANY_ATTEMPTS' ||
        directErr?.status === 401 ||
        directErr?.status === 403 ||
        directErr?.status === 429
      ) {
        setErrorMsg(directErr.message || 'Invalid administrator email or password.');
        return;
      }
      setErrorMsg(directErr.message || 'Administrator sign-in failed. Please verify credentials.');
      return;
    }

    try {
      const fbResult = await adminSignInWithFirebase(email.trim(), password);
      if (fbResult.isAdmin || fbResult.hasPrimaryAdminRole) {
        setAdminToken(fbResult.idToken);
        const role = fbResult.role || 'SUPER_ADMIN';
        setPendingAdminRole(role);

        // Security rule: APITXT SMS OTP is strictly required before accessing admin dashboard
        if (fbResult.mfaRequired !== false) {
          setMfaStep('SERVER_SMS_OTP');
          setErrorMsg(null);
          setIsLoading(false);
          const phoneCandidate = fbResult.phone || undefined;
          if (phoneCandidate) {
            setMfaPhone(phoneCandidate);
          }
          try {
            const smsRes = await sendAdminMfaOtpApi(phoneCandidate);
            setMfaOtpSent(true);
            setMfaMaskedPhone(smsRes?.maskedPhone || null);
            setMfaVerificationId(smsRes?.verificationId || null);
          } catch (smsErr: any) {
            setErrorMsg(
              smsErr?.code === 'ADMIN_PHONE_REQUIRED'
                ? 'Enter your registered administrator mobile number to receive a 2FA code.'
                : smsErr?.message || 'Enter your registered administrator mobile number to receive a 2FA code.'
            );
          }
          return;
        }

        // If policy explicitly disabled MFA (mfa_enabled = false), verify server session
        try {
          const meRes = await fetchAdminMeApi();
          if (!meRes?.success || !meRes?.admin) {
            throw new Error('Server-side administrator session verification did not succeed.');
          }
          navigate('/admin');
          return;
        } catch (meErr: any) {
          if (
            meErr?.code === 'ADMIN_MFA_REQUIRED' ||
            (meErr?.status === 403 &&
              (meErr?.code === 'ADMIN_MFA_REQUIRED' ||
                String(meErr?.message || '').toLowerCase().includes('multi-factor') ||
                String(meErr?.message || '').toLowerCase().includes('2fa')))
          ) {
            setMfaStep('SERVER_SMS_OTP');
            setErrorMsg(null);
            setIsLoading(false);
            const phoneCandidate = fbResult.phone || undefined;
            if (phoneCandidate) {
              setMfaPhone(phoneCandidate);
            }
            try {
              const smsRes = await sendAdminMfaOtpApi(phoneCandidate);
              setMfaOtpSent(true);
              setMfaMaskedPhone(smsRes?.maskedPhone || null);
              setMfaVerificationId(smsRes?.verificationId || null);
            } catch (smsErr: any) {
              setErrorMsg(
                smsErr?.code === 'ADMIN_PHONE_REQUIRED'
                  ? 'Enter your registered administrator mobile number to receive a 2FA code.'
                  : smsErr?.message || 'Enter your registered administrator mobile number to receive a 2FA code.'
              );
            }
            return;
          }
          throw meErr;
        }
      } else {
        clearAdminToken();
        await signOutUser().catch(() => null);
        setErrorMsg('Access denied. This Firebase account has no active server-provisioned administrator role.');
      }
    } catch (err: any) {
      if (err?.code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(
            auth,
            err as MultiFactorError
          );
          setMfaResolver(resolver);

          const phoneHint = resolver.hints.find(
            (hint) =>
              hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID ||
              (hint as any).factorId === 'phone'
          ) as PhoneMultiFactorInfo | undefined;

          const totpHint = resolver.hints.find(
            (hint) =>
              String(hint.factorId) === TotpMultiFactorGenerator.FACTOR_ID ||
              String(hint.factorId) === 'totp'
          );

          if (phoneHint) {
            setSelectedPhoneHint(phoneHint);
            setMfaStep('FIREBASE_PHONE');
            setErrorMsg(null);
          } else if (totpHint) {
            setMfaStep('FIREBASE_TOTP');
            setErrorMsg(null);
          } else {
            const types = resolver.hints.map((factor) => factor.factorId).join(', ');
            setErrorMsg(
              `Additional authentication required. Factor type(s): ${types || 'not reported'}.`
            );
          }
        } catch {
          setErrorMsg(
            'Firebase requires additional authentication, but the enrolled factor details could not be read.'
          );
        }
      } else {
        setErrorMsg(formatFirebaseAdminAuthError(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendFirebasePhoneMfaCode = async () => {
    if (isSendingFirebasePhoneOtp) {
      return;
    }
    if (!mfaResolver || !selectedPhoneHint) {
      setErrorMsg('No Firebase phone multi-factor session or hint found.');
      return;
    }
    setIsSendingFirebasePhoneOtp(true);
    setErrorMsg(null);
    try {
      const appVerifier = createFreshRecaptchaVerifier();
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          multiFactorHint: selectedPhoneHint,
          multiFactorUid: selectedPhoneHint.uid,
          session: mfaResolver.session,
        },
        appVerifier
      );
      setFirebaseVerificationId(verificationId);
    } catch (err: any) {
      clearRecaptchaVerifier();
      const code = String(err?.code || 'auth/unknown');
      const rawServerMessage = typeof err?.customData?._serverResponse?.error?.message === 'string'
        ? err.customData._serverResponse.error.message
        : typeof err?.message === 'string'
        ? err.message
        : '';

      // Sanitized diagnostic logging: never log raw error objects, server responses, tokens, or credentials
      console.warn('[AdminLoginPage] Phone MFA verification challenge rejected:', {
        errorCode: code,
        sanitizedMessage: rawServerMessage,
      });

      if (code === 'auth/invalid-app-credential' || rawServerMessage.includes('INVALID_APP_CREDENTIAL')) {
        setErrorMsg(
          'Firebase application verification failed (auth/invalid-app-credential). The Identity Platform backend rejected the app verification token. Please verify that this project has enabled SMS Phone Authentication, valid reCAPTCHA keys, or registered test numbers in Firebase Console.'
        );
      } else if (rawServerMessage.includes('INVALID_PENDING_TOKEN') || code === 'auth/invalid-credential') {
        setErrorMsg(
          'Your multi-factor sign-in session has expired. Please click "← Back to Admin Credentials" below to sign in again.'
        );
      } else if (code === 'auth/captcha-check-failed') {
        setErrorMsg(
          'reCAPTCHA verification challenge failed. Please refresh the page and try again.'
        );
      } else {
        setErrorMsg(
          rawServerMessage || err?.message || 'Failed to dispatch Firebase phone MFA verification code. Please try again.'
        );
      }
    } finally {
      setIsSendingFirebasePhoneOtp(false);
    }
  };

  const handleSendServerMfaOtp = async () => {
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const res = await sendAdminMfaOtpApi(mfaPhone.trim() || undefined);
      setMfaOtpSent(true);
      setMfaMaskedPhone(res?.maskedPhone || null);
      setMfaVerificationId(res?.verificationId || null);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to dispatch administrator 2FA SMS OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaCode.trim()) {
      setErrorMsg('Please enter your 6-digit 2FA verification code.');
      return;
    }
    setIsLoading(true);
    setErrorMsg(null);
    try {
      if (mfaStep === 'FIREBASE_PHONE' && mfaResolver) {
        if (!firebaseVerificationId) {
          throw new Error('Please request a Firebase phone verification code first.');
        }
        const cred = PhoneAuthProvider.credential(firebaseVerificationId, mfaCode.trim());
        const assertion = PhoneMultiFactorGenerator.assertion(cred);
        const userCred = await mfaResolver.resolveSignIn(assertion);
        const status = await checkCurrentUserAdminStatus(userCred.user);
        const freshToken = await userCred.user.getIdToken(true);
        if (!status.isAdmin) {
          clearAdminToken();
          await signOutUser().catch(() => null);
          throw new Error('Access denied: Administrator role could not be confirmed.');
        }
        setAdminToken(freshToken);

        // Security rule: Firebase native second factor is NOT a substitute for APITXT SMS OTP.
        // Transition directly to the authoritative server-side APITXT SMS OTP challenge.
        setPendingAdminRole(status.role || 'SUPER_ADMIN');
        setMfaStep('SERVER_SMS_OTP');
        setIsLoading(false);
        const phoneCandidate = userCred.user.phoneNumber || status.phone;
        if (phoneCandidate) {
          setMfaPhone(phoneCandidate);
          try {
            const smsRes = await sendAdminMfaOtpApi(phoneCandidate);
            setMfaOtpSent(true);
            setMfaMaskedPhone(smsRes?.maskedPhone || null);
            setMfaVerificationId(smsRes?.verificationId || null);
          } catch (smsErr: any) {
            setErrorMsg(smsErr?.message || 'Enter your registered administrator mobile number to receive a 2FA code.');
          }
        }
        return;
      }

      if (mfaStep === 'FIREBASE_TOTP' && mfaResolver) {
        const totpHint = mfaResolver.hints.find(
          (hint) => String(hint.factorId) === TotpMultiFactorGenerator.FACTOR_ID || String(hint.factorId) === 'totp'
        );
        if (!totpHint) {
          throw new Error('No TOTP authenticator factor found on this administrator account.');
        }
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, mfaCode.trim());
        const userCred = await mfaResolver.resolveSignIn(assertion);
        const status = await checkCurrentUserAdminStatus(userCred.user);
        const freshToken = await userCred.user.getIdToken(true);
        if (!status.isAdmin) {
          clearAdminToken();
          await signOutUser().catch(() => null);
          throw new Error('Access denied: Administrator role could not be confirmed.');
        }
        setAdminToken(freshToken);

        // Security rule: Firebase native second factor is NOT a substitute for APITXT SMS OTP.
        // Transition directly to the authoritative server-side APITXT SMS OTP challenge.
        setPendingAdminRole(status.role || 'SUPER_ADMIN');
        setMfaStep('SERVER_SMS_OTP');
        setIsLoading(false);
        const phoneCandidate = userCred.user.phoneNumber || status.phone;
        if (phoneCandidate) {
          setMfaPhone(phoneCandidate);
          try {
            const smsRes = await sendAdminMfaOtpApi(phoneCandidate);
            setMfaOtpSent(true);
            setMfaMaskedPhone(smsRes?.maskedPhone || null);
            setMfaVerificationId(smsRes?.verificationId || null);
          } catch (smsErr: any) {
            setErrorMsg(smsErr?.message || 'Enter your registered administrator mobile number to receive a 2FA code.');
          }
        }
        return;
      }

      if (mfaStep === 'SERVER_SMS_OTP') {
        const verifyRes = await verifyAdminMfaOtpApi(
          mfaCode.trim(),
          mfaPhone.trim() || undefined,
          mfaVerificationId || undefined
        );
        if (verifyRes?.sessionId && (verifyRes?.expiresAt || verifyRes?.mfaVerifiedUntilMs)) {
          setAdminMfaSession(verifyRes.sessionId, verifyRes.expiresAt || verifyRes.mfaVerifiedUntilMs);
        }
        const currentUser = auth.currentUser;
        if (currentUser) {
          const freshToken = await currentUser.getIdToken(true);
          setAdminToken(freshToken);
        }
        setStoredAdminInfo({
          email: currentUser?.email || email,
          name: currentUser?.displayName || 'Administrator',
          role: verifyRes?.admin?.role || pendingAdminRole || 'SUPER_ADMIN',
          source: 'server_mfa_verified',
          mfaVerified: true,
        });
        const meRes = await fetchAdminMeApi();
        if (!meRes?.success || !meRes?.admin) {
          throw new Error('Server-side administrator session verification did not succeed.');
        }
        navigate('/admin');
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'Administrator 2FA verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetResetEmail = (resetEmail || email).trim();
    if (!targetResetEmail) {
      setResetErrorMsg('Please enter your registered administrator email address.');
      return;
    }
    setResetSending(true);
    setResetErrorMsg(null);
    setResetStatusMsg(null);
    try {
      await sendAdminPasswordReset(targetResetEmail);
      setResetStatusMsg(
        'If this email belongs to an active administrator account, a secure Firebase password-reset link has been sent. Please check your inbox and spam folder.'
      );
    } catch (err: any) {
      setResetErrorMsg(formatFirebaseAdminAuthError(err));
    } finally {
      setResetSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 font-sans text-slate-100 flex flex-col justify-between selection:bg-purple-600 selection:text-white">
      {/* Top Header */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <PairUpLogo className="w-10 h-10 shrink-0" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-black text-sm tracking-tight text-white uppercase">
                PairUp Today
              </span>
              <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-[10px] font-mono font-bold">
                ISOLATED ADMIN PORTAL
              </span>
            </div>
            <p className="text-[11px] text-slate-400">Master Operations & Administrative Control Gateway</p>
          </div>
        </div>

        <Link
          to="/"
          className="text-xs text-slate-400 hover:text-white transition-colors"
        >
          Exit to Public Site →
        </Link>
      </header>

      {/* Main Login Card */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 my-8">
        <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 relative overflow-hidden">
          {/* Subtle Ambient Glow */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />

          {/* Heading with Prominent Golden Logo */}
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-2">
              <PairUpLogo className="w-32 h-32" />
            </div>
            <h1 className="text-2xl font-black text-white tracking-tight">
              PairUpToday Admin Login
            </h1>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              Completely isolated administrative environment. Client and Companion accounts are strictly prohibited.
            </p>
          </div>

          {/* If already authed banner */}
          {alreadyAuthed && (
            <div className="p-3 bg-purple-950/60 border border-purple-700/50 rounded-2xl flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-purple-200">
                <CheckCircle2 className="w-4 h-4 text-purple-400 shrink-0" />
                <span>Active admin session found!</span>
              </div>
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="px-3 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-bold text-xs cursor-pointer"
              >
                Go to Dashboard
              </button>
            </div>
          )}

          {/* Error Banner */}
          {errorMsg && (
            <div className="p-3.5 rounded-2xl bg-rose-950/80 border border-rose-800 text-xs font-semibold text-rose-200 flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Invisible reCAPTCHA container for Firebase Phone MFA (permanently mounted) */}
          <div ref={recaptchaContainerRef} id="firebase-phone-recaptcha-container" />

          {/* Admin Form or Mandatory 2FA Verification Step */}
          {mfaStep === 'NONE' ? (
            <form onSubmit={handleAdminSubmit} className="space-y-4">
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-purple-950/40 border border-purple-800/40 text-purple-300 text-xs font-mono">
                <span className="flex items-center gap-1.5 font-bold">
                  <Flame className="w-4 h-4 text-amber-400" /> Firebase Auth + RBAC + 2FA Guard
                </span>
                <span className="text-[10px] bg-purple-900/60 px-2 py-0.5 rounded text-purple-200">
                  2FA Required
                </span>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 font-mono">
                  Admin Email ID
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@pairuptoday.com"
                    className="w-full pl-10 pr-3 py-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-semibold text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 font-mono">
                    Admin Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setForgotPasswordModalOpen(true)}
                    className="text-[11px] text-purple-400 hover:text-purple-300 transition cursor-pointer"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="w-full pl-10 pr-10 py-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-semibold text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember Session Checkbox */}
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="rememberAdminSession"
                  checked={rememberSession}
                  onChange={(e) => setRememberSession(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-800 text-purple-600 focus:ring-purple-500 bg-slate-950 cursor-pointer"
                />
                <label htmlFor="rememberAdminSession" className="text-xs text-slate-400 cursor-pointer select-none">
                  Remember administrative session on this device
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 hover:from-purple-500 hover:to-indigo-500 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-purple-950 transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>{isLoading ? 'Verifying Authorization...' : 'Login to Admin Panel'}</span>
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyMfaSubmit} className="space-y-4">
              <div className="p-3 rounded-xl bg-purple-950/50 border border-purple-700/50 text-purple-200 text-xs space-y-1">
                <div className="flex items-center gap-2 font-bold">
                  <Smartphone className="w-4 h-4 text-purple-400" />
                  <span>
                    {mfaStep === 'FIREBASE_TOTP'
                      ? 'Step 2: Authenticator App (TOTP) 2FA Required'
                      : mfaStep === 'FIREBASE_PHONE'
                      ? 'Step 2: Firebase Phone Multi-Factor Authentication'
                      : 'Step 2: Mandatory Administrator SMS 2FA Verification'}
                  </span>
                </div>
                <p className="text-[11px] text-slate-300">
                  {mfaStep === 'FIREBASE_TOTP'
                    ? 'Enter the 6-digit verification code from your enrolled TOTP authenticator application.'
                    : mfaStep === 'FIREBASE_PHONE'
                    ? selectedPhoneHint?.phoneNumber
                      ? `Firebase Phone MFA enrolled for ${selectedPhoneHint.phoneNumber}. Click below to dispatch your Firebase verification code.`
                      : 'Firebase Phone MFA enrolled. Click below to dispatch your 6-digit verification code.'
                    : mfaMaskedPhone
                    ? `A 6-digit 2FA verification code was dispatched to ${mfaMaskedPhone}.`
                    : 'Verify your registered administrator mobile number to receive a 6-digit 2FA code.'}
                </p>
              </div>

              {mfaStep === 'FIREBASE_PHONE' && (
                <div className="space-y-2">
                  <button
                    type="button"
                    disabled={isLoading || isSendingFirebasePhoneOtp}
                    onClick={handleSendFirebasePhoneMfaCode}
                    className="w-full py-2.5 bg-purple-700/60 hover:bg-purple-600 text-purple-100 font-bold text-xs rounded-xl border border-purple-500/40 cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Smartphone className="w-4 h-4" />
                    <span>
                      {isSendingFirebasePhoneOtp
                        ? 'Sending Firebase Code...'
                        : firebaseVerificationId
                        ? 'Resend Firebase Verification Code'
                        : 'Send Firebase Verification Code'}
                    </span>
                  </button>
                  {firebaseVerificationId && (
                    <p className="text-[11px] text-emerald-400 font-semibold text-center">
                      Verification SMS dispatched! Enter the 6-digit code below.
                    </p>
                  )}
                </div>
              )}

              {mfaStep === 'SERVER_SMS_OTP' && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 font-mono">
                    Registered Admin Mobile Number
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      value={mfaPhone}
                      onChange={(e) => setMfaPhone(e.target.value)}
                      placeholder="10-digit Indian mobile number"
                      className="flex-1 px-3.5 py-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-semibold text-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                    />
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={handleSendServerMfaOtp}
                      className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-purple-300 font-bold text-xs rounded-xl border border-purple-700/40 cursor-pointer disabled:opacity-50"
                    >
                      {mfaOtpSent ? 'Resend OTP' : 'Send 2FA OTP'}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 font-mono">
                  6-Digit 2FA Verification Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-center tracking-[0.4em] text-base font-black text-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || mfaCode.length !== 6}
                className="w-full py-3.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-emerald-950 transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>{isLoading ? 'Verifying 2FA Code...' : 'Verify 2FA & Enter Admin Panel'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  clearRecaptchaVerifier();
                  setMfaStep('NONE');
                  setMfaResolver(null);
                  setSelectedPhoneHint(null);
                  setFirebaseVerificationId(null);
                  setMfaCode('');
                  setMfaOtpSent(false);
                  clearAdminToken();
                  signOutUser().catch(() => null);
                }}
                className="w-full py-2 text-xs text-slate-400 hover:text-white transition cursor-pointer"
              >
                ← Back to Admin Credentials
              </button>
            </form>
          )}

          <p className="text-center text-[11px] text-slate-500">Access is limited to active Firebase administrator accounts provisioned by a trusted server process.</p>

          {/* Security Compliance Audit Notice */}
          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/60 flex items-start gap-2 text-[10px] text-slate-400 leading-relaxed font-mono">
            <Terminal className="w-3.5 h-3.5 text-purple-400 shrink-0 mt-0.5" />
            <span>
              CRITICAL: Client or Companion tokens cannot invoke Admin APIs. All attempts are rejected with 403 Forbidden and appended to immutable audit logs.
            </span>
          </div>
        </div>
      </main>

      {/* Forgot Password Modal */}
      {forgotPasswordModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <HelpCircle className="w-5 h-5 text-purple-400" />
                <span>Admin Password Reset</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setForgotPasswordModalOpen(false);
                  setResetErrorMsg(null);
                  setResetStatusMsg(null);
                }}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              Send a verified Firebase Authentication password-reset link to your registered administrator email address. After resetting your password, 2FA verification will still be enforced on sign-in.
            </p>
            {resetErrorMsg && (
              <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-800 text-xs text-rose-200">
                {resetErrorMsg}
              </div>
            )}
            {resetStatusMsg && (
              <div className="p-3 rounded-xl bg-emerald-950/80 border border-emerald-800 text-xs text-emerald-200">
                {resetStatusMsg}
              </div>
            )}
            <form onSubmit={handlePasswordResetSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 font-mono">
                  Verified Admin Email
                </label>
                <input
                  type="email"
                  required
                  value={resetEmail || email}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="rahulfilmsindia@gmail.com"
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs font-semibold text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={resetSending}
                  className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold cursor-pointer disabled:opacity-50"
                >
                  {resetSending ? 'Sending Reset Link...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordModalOpen(false);
                    setResetErrorMsg(null);
                    setResetStatusMsg(null);
                  }}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold cursor-pointer"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-slate-800/60 py-4 px-6 text-center text-xs text-slate-400 flex flex-col sm:flex-row items-center justify-between gap-2 max-w-7xl mx-auto w-full">
        <div>PairUpToday Production Administration Node • Master Spec v1.0</div>
        <div className="flex items-center gap-4 text-[11px]">
          <span className="flex items-center gap-1.5 text-emerald-400">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Core Server Online
          </span>
          <Link to="/login" className="hover:text-white transition-colors">
            User Login Portal
          </Link>
        </div>
      </footer>
    </div>
  );
};
